import { LegalLayout, Section } from "@/pages/legal/LegalLayout";
import { COMPANY } from "@/lib/site-data";

const Privacy = () => {
  return (
    <LegalLayout
      testid="page-privacy"
      title="Privacy Policy"
      updated="December 2025"
      lead="This policy explains how Dream Technology sp. z o.o. collects, uses and protects personal data, in accordance with Regulation (EU) 2016/679 (GDPR / RODO) and the Polish Personal Data Protection Act of 10 May 2018."
    >
      <Section number="01" title="Controller">
        <p>
          The controller of your personal data is{" "}
          <strong>{COMPANY.legalName}</strong>, with its registered office at{" "}
          {COMPANY.hqAddress}, NIP / REGON{" "}
          <span className="font-mono">{COMPANY.regon}</span>.
        </p>
        <p>
          You can contact the controller in matters relating to your personal
          data by writing to{" "}
          <a
            href={`mailto:${COMPANY.email}`}
            className="text-emerald-800 underline underline-offset-2"
          >
            {COMPANY.email}
          </a>{" "}
          or by post to the address above with the subject line “<em>Dane osobowe / Data Protection</em>”.
        </p>
      </Section>

      <Section number="02" title="What data we process">
        <p>Depending on your interaction with us, we may process:</p>
        <ul className="list-disc pl-6 space-y-2">
          <li>
            <strong>Contact-form data</strong> — name, work email, company
            name, industry of interest and the content of your message,
            submitted voluntarily via the form on {COMPANY.domain};
          </li>
          <li>
            <strong>Correspondence data</strong> — email exchanges and other
            written communications initiated by you or on your behalf;
          </li>
          <li>
            <strong>Engagement data</strong> — information relating to the
            representatives of Clients and prospective Clients, including
            business role, professional contact details and information
            relevant to the services;
          </li>
          <li>
            <strong>Technical data</strong> — IP address, browser and device
            metadata and aggregated usage statistics collected via privacy-
            respecting analytics for security and website-improvement purposes.
          </li>
        </ul>
      </Section>

      <Section number="03" title="Purposes and legal bases">
        <p>We process personal data for the following purposes:</p>
        <ul className="list-disc pl-6 space-y-2">
          <li>
            responding to inquiries submitted via the contact form —{" "}
            <em>Art. 6(1)(b) GDPR</em> (steps prior to entering into a
            contract) and <em>Art. 6(1)(f) GDPR</em> (our legitimate interest
            in operating our business);
          </li>
          <li>
            performing services under a signed agreement —{" "}
            <em>Art. 6(1)(b) GDPR</em>;
          </li>
          <li>
            complying with Polish tax, accounting and archival obligations —{" "}
            <em>Art. 6(1)(c) GDPR</em>;
          </li>
          <li>
            establishing, exercising or defending legal claims —{" "}
            <em>Art. 6(1)(f) GDPR</em>;
          </li>
          <li>
            ensuring the security and availability of our website and IT
            systems — <em>Art. 6(1)(f) GDPR</em>.
          </li>
        </ul>
        <p>
          Providing personal data is voluntary but may be necessary to receive
          a response or enter into a service agreement.
        </p>
      </Section>

      <Section number="04" title="Retention">
        <p>
          Personal data are retained only for as long as necessary for the
          purposes set out above:
        </p>
        <ul className="list-disc pl-6 space-y-2">
          <li>
            inquiry data — up to 24 months from the last correspondence, unless
            an engagement is opened;
          </li>
          <li>
            engagement data — for the duration of the engagement and for the
            statutory limitation period afterwards (generally up to 6 years,
            in accordance with Art. 118 of the Polish Civil Code);
          </li>
          <li>
            accounting documents — 5 years from the end of the fiscal year in
            which they arose (Art. 74 of the Polish Accounting Act);
          </li>
          <li>
            security logs — up to 12 months, unless required longer for
            investigating an incident.
          </li>
        </ul>
      </Section>

      <Section number="05" title="Recipients & processors">
        <p>
          We may share personal data with carefully selected processors acting
          on our behalf, in particular providers of email, cloud hosting,
          database, analytics and productivity services. Each processor is
          bound by a written data-processing agreement compliant with{" "}
          <em>Art. 28 GDPR</em>. We do not sell personal data to third parties.
        </p>
      </Section>

      <Section number="06" title="Transfers outside the EEA">
        <p>
          Where personal data are transferred to processors located outside
          the European Economic Area, we ensure an adequate level of
          protection either through European Commission adequacy decisions or
          through Standard Contractual Clauses (SCCs) supplemented by
          additional safeguards as required. On request we can provide details
          of the specific safeguards in place.
        </p>
      </Section>

      <Section number="07" title="Your rights">
        <p>Under GDPR you have the right to:</p>
        <ul className="list-disc pl-6 space-y-2">
          <li>access your personal data (Art. 15);</li>
          <li>request rectification of inaccurate data (Art. 16);</li>
          <li>request erasure (Art. 17), subject to legal exceptions;</li>
          <li>request restriction of processing (Art. 18);</li>
          <li>data portability where technically feasible (Art. 20);</li>
          <li>
            object to processing based on legitimate interests, including
            profiling (Art. 21);
          </li>
          <li>
            withdraw consent at any time, where processing is based on consent
            (Art. 7(3)), without affecting past lawful processing.
          </li>
        </ul>
        <p>
          You also have the right to lodge a complaint with the Polish
          supervisory authority: <em>Prezes Urzędu Ochrony Danych Osobowych</em>{" "}
          (President of the Personal Data Protection Office), ul. Stawki 2,
          00-193 Warsaw.
        </p>
      </Section>

      <Section number="08" title="Cookies & website analytics">
        <p>
          We use only essential cookies required for the website to function
          and, where applicable, privacy-preserving analytics to understand
          aggregate usage and improve the experience. We do not use marketing
          or advertising cookies. You can restrict or delete cookies via your
          browser settings at any time.
        </p>
      </Section>

      <Section number="09" title="Security">
        <p>
          We apply appropriate technical and organisational measures to
          protect personal data against unauthorised access, loss or
          destruction, in line with{" "}
          <em>Art. 32 GDPR</em> and internal ISO 27001-aligned controls.
        </p>
      </Section>

      <Section number="10" title="Changes to this policy">
        <p>
          We may amend this policy where legal, technological or organisational
          changes require it. The current version is always available at{" "}
          {COMPANY.domain}/legal/privacy with the “last updated” date shown
          above.
        </p>
      </Section>
    </LegalLayout>
  );
};

export default Privacy;
